import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAlgo } from '@/hooks/useAlgo';
import Form from '@/components/Form';
import Input from '@/components/Input';
import Textarea from '@/components/Textarea';
import FormSelect from '@/components/FormSelect';
import InputWrapper from '@/components/InputWrapper';
import InputLabel from '@/components/InputLabel';
import FormActionsWrapper from '@/components/FormActionsWrapper';
import Button from '@/components/Button';
import Alert from '@/components/Alert';

const categories = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const SupportForm = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const { address } = useAlgo();

  const form = useForm({
    defaultValues: {
      category: 'general',
      email: '',
      subject: '',
      message: '',
    },
  });

  const handleSubmit = async (data) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/support/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({
          ...data,
          walletAddress: address,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Something went wrong');
      }

      setSuccess(true);
      form.reset();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative'>
      <div className='absolute -inset-1 bg-gradient-to-r from-primary-500/20 via-secondary-500/20 to-primary-500/20 rounded-2xl blur-xl opacity-50' />

      <div className='relative bg-gray-900/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gray-800'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center'>
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' className='w-6 h-6 text-neutral-900'>
              <path
                fillRule='evenodd'
                d='M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div>
            <h2 className='text-xl font-bold text-neutral-100'>Send us a message</h2>
            <p className='text-sm text-neutral-400'>We typically respond within 24 hours</p>
          </div>
        </div>

        {success && (
          <InputWrapper>
            <div className='p-4 rounded-md bg-primary-500 text-neutral-900 font-medium'>Message sent successfully! We'll get back to you soon.</div>
          </InputWrapper>
        )}

        {error && (
          <InputWrapper>
            <Alert type='danger'>{error}</Alert>
          </InputWrapper>
        )}

        <Form onSubmit={handleSubmit} form={form}>
          <InputWrapper>
            <InputLabel>Category</InputLabel>
            <FormSelect name='category' options={categories} required className='!w-full' />
          </InputWrapper>

          <InputWrapper>
            <InputLabel>
              Email Address <span className='text-neutral-500 font-normal'>(optional)</span>
            </InputLabel>
            <Input type='email' name='email' placeholder='your@email.com' fullWidth />
          </InputWrapper>

          <InputWrapper>
            <InputLabel>Subject</InputLabel>
            <Input type='text' name='subject' placeholder="What's this about?" required fullWidth />
          </InputWrapper>

          <InputWrapper>
            <InputLabel>Message</InputLabel>
            <Textarea name='message' placeholder='Tell us more... The more detail, the better we can help!' required fullWidth rows={6} />
          </InputWrapper>

          <FormActionsWrapper>
            <Button loading={loading} type='submit' className='w-full md:w-auto'>
              Send Message
            </Button>
          </FormActionsWrapper>
        </Form>
      </div>
    </div>
  );
};

export default SupportForm;
